import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    useConfirmRefurbishedOrderMutation,
    useGetPendingOrderQuery,
} from "../../../common/api/refurbished";
import { useGetUserInfoQuery } from "../../../app/rootReducer";
import { RouteEnum } from "../../../common/enums/RouteEnum";
import { ConfirmRefurbishedOrderRequestInterface } from "../../../common/interfaces/apiV2/refurbished/RefurbishedConfirmOrderInterface";
import { useCheckoutLabels } from "./useCheckoutLabels";

export interface CheckoutFormState {
    address: string;
    postal_code: string;
    city: string;
    country_id: string;
    additional_notes: string;
}

export interface CheckoutFormErrors {
    address?: string;
    postal_code?: string;
    city?: string;
    country_id?: string;
}

const ADDRESS_FIELDS_RESET_PRIMARY: (keyof CheckoutFormState)[] = [
    "address",
    "postal_code",
    "city",
    "country_id",
];

export interface UseCheckoutFormReturn {
    form: CheckoutFormState;
    errors: CheckoutFormErrors;
    isPrimary: boolean;
    isValid: boolean;
    isSubmitting: boolean;
    submitError: string | null;
    updateField: (field: keyof CheckoutFormState, value: string) => void;
    setIsPrimary: (value: boolean) => void;
    handleSubmit: () => Promise<void>;
}

export const useCheckoutForm = (): UseCheckoutFormReturn => {
    const navigate = useNavigate();
    const labels = useCheckoutLabels();
    const { data: userData } = useGetUserInfoQuery();
    const { data: pendingOrderResponse } = useGetPendingOrderQuery();
    const [confirmOrder, { isLoading: isSubmitting }] =
        useConfirmRefurbishedOrderMutation();

    const pendingOrder = pendingOrderResponse?.data ?? null;

    const [form, setForm] = useState<CheckoutFormState>({
        address: "",
        postal_code: "",
        city: "",
        country_id: "",
        additional_notes: "",
    });
    const [errors, setErrors] = useState<CheckoutFormErrors>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isPrimary, setIsPrimary] = useState(false);

    useEffect(() => {
        const deliveryAddress = userData?.delivery_address;
        const activeCountryId = userData?.activeCountry?.id;

        if (!deliveryAddress && !activeCountryId) {
            return;
        }

        setForm((current) => ({
            address: deliveryAddress?.address ?? current.address,
            postal_code: deliveryAddress?.postal_code ?? current.postal_code,
            city: deliveryAddress?.city ?? current.city,
            country_id: deliveryAddress?.country_id
                ? String(deliveryAddress.country_id)
                : activeCountryId
                  ? String(activeCountryId)
                  : current.country_id,
            additional_notes:
                deliveryAddress?.additional_notes ?? current.additional_notes,
        }));

        if (deliveryAddress?.address) {
            setIsPrimary(true);
        }
    }, [userData?.delivery_address, userData?.activeCountry?.id]);

    useEffect(() => {
        if (
            pendingOrderResponse !== undefined &&
            (!pendingOrder || !pendingOrder.order_lines.length)
        ) {
            navigate(RouteEnum.REFURBISHED_CART, { replace: true });
        }
    }, [pendingOrder, pendingOrderResponse, navigate]);

    const updateField = useCallback(
        (field: keyof CheckoutFormState, value: string) => {
            setForm((current) => ({ ...current, [field]: value }));
            if (ADDRESS_FIELDS_RESET_PRIMARY.includes(field)) {
                setIsPrimary(false);
            }
            setErrors((current) => ({ ...current, [field]: undefined }));
            setSubmitError(null);
        },
        [],
    );

    const setIsPrimaryValue = useCallback((value: boolean) => {
        setIsPrimary(value);
        setSubmitError(null);
    }, []);

    const validate = useCallback((): CheckoutFormErrors => {
        const nextErrors: CheckoutFormErrors = {};

        if (!form.address.trim()) {
            nextErrors.address = labels.requiredField;
        }
        if (!form.postal_code.trim()) {
            nextErrors.postal_code = labels.requiredField;
        }
        if (!form.city.trim()) {
            nextErrors.city = labels.requiredField;
        }
        if (!form.country_id) {
            nextErrors.country_id = labels.requiredField;
        }

        return nextErrors;
    }, [form, labels.requiredField]);

    const isValid = useMemo(() => {
        return (
            !!form.address.trim() &&
            !!form.postal_code.trim() &&
            !!form.city.trim() &&
            !!form.country_id
        );
    }, [form]);

    const handleSubmit = useCallback(async () => {
        const validationErrors = validate();
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0 || !pendingOrder) {
            return;
        }

        const payload: ConfirmRefurbishedOrderRequestInterface = {
            address: form.address.trim(),
            postal_code: form.postal_code.trim(),
            city: form.city.trim(),
            country_id: Number(form.country_id),
            is_primary: isPrimary,
            additional_notes: form.additional_notes.trim() || undefined,
        };

        try {
            const result = await confirmOrder({
                orderId: pendingOrder.id,
                payload,
            }).unwrap();

            navigate(RouteEnum.REFURBISHED_CHECKOUT_THANK_YOU, {
                state: { order: result.data },
                replace: true,
            });
        } catch {
            setSubmitError(labels.submitError);
        }
    }, [
        validate,
        pendingOrder,
        form,
        isPrimary,
        confirmOrder,
        navigate,
        labels.submitError,
    ]);

    return {
        form,
        errors,
        isPrimary,
        isValid,
        isSubmitting,
        submitError,
        updateField,
        setIsPrimary: setIsPrimaryValue,
        handleSubmit,
    };
};
